import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, StaticRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { LazyMotion, MotionConfig, domAnimation } from 'motion/react';
import { PostHogPageview } from '@/components/PostHogPageview';
import { ScrollToTop } from '@/components/ScrollToTop';
import { Sentry } from '@/lib/sentry';
import { I18nProvider } from '@/i18n/i18n';
import Index from './pages/Index';
import Biura from './pages/Biura';
import Jdg from './pages/Jdg';
import KsefAutomatycznie from './pages/KsefAutomatycznie';
import KsiegowoscAi from './pages/KsiegowoscAi';
import Wdrozenia from './pages/Wdrozenia';
import NotFound from './pages/NotFound';
import Privacy from '@/pages/Privacy';
import Terms from '@/pages/Terms';
import Cookies from '@/pages/Cookies';
import { ProxyOwnedLegalDocument } from '@/components/ProxyOwnedLegalDocument';
import KpirElektroniczna2026 from '@/pages/przewodniki/KpirElektroniczna2026';
import KodPkdJakWybrac from '@/pages/przewodniki/KodPkdJakWybrac';
import KsefDlaJdg from '@/pages/przewodniki/KsefDlaJdg';
import KsiegowoscOnline from '@/pages/przewodniki/KsiegowoscOnline';
import EDoreczeniaJdg2026 from '@/pages/przewodniki/EDoreczeniaJdg2026';
import EwidencjaPrzychodowRyczalt2026 from '@/pages/blog/EwidencjaPrzychodowRyczalt2026';
import Mcp from '@/pages/Mcp';

const queryClient = new QueryClient();

const sentryFallback = (
  <div className="flex min-h-screen items-center justify-center p-8 text-center">
    <div>
      <h1 className="text-2xl font-semibold">Cos poszlo nie tak</h1>
      <p className="mt-2 text-muted-foreground">
        Sprobuj odswiezyc strone. Jesli problem sie powtarza, skontaktuj sie z nami.
      </p>
      <button
        onClick={() => window.location.reload()}
        className="mt-4 rounded-md bg-primary px-4 py-2 text-primary-foreground"
      >
        Odswiez strone
      </button>
    </div>
  </div>
);

/** All app routes, rendered inside I18nProvider */
function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Index />} />
      <Route path="/privacy" element={<ProxyOwnedLegalDocument path="/polityka-prywatnosci" />} />
      <Route path="/terms" element={<ProxyOwnedLegalDocument path="/regulamin" />} />
      <Route
        path="/polityka-prywatnosci"
        element={<ProxyOwnedLegalDocument path="/polityka-prywatnosci" />}
      />
      <Route path="/regulamin" element={<ProxyOwnedLegalDocument path="/regulamin" />} />
      <Route path="/cookies" element={<Cookies />} />
      <Route path="/biura" element={<Biura />} />
      <Route path="/jdg" element={<Jdg />} />
      <Route path="/wdrozenia" element={<Wdrozenia />} />
      <Route path="/ksef-automatycznie" element={<KsefAutomatycznie />} />
      {/* Polish-only: /en and /uk render the same page and canonicalise to this URL. */}
      <Route path="/ksiegowosc-ai" element={<KsiegowoscAi />} />

      {/* Przewodniki (AI-SEO explainers). Content is Polish on every locale
          route and each page canonicalises to the unprefixed URL — the /en and
          /uk mirrors exist so localized navigation never 404s, not to rank. */}
      <Route path="/przewodniki/kpir-elektroniczna-2026" element={<KpirElektroniczna2026 />} />
      <Route path="/przewodniki/kod-pkd-jak-wybrac" element={<KodPkdJakWybrac />} />
      <Route path="/przewodniki/ksef-dla-jdg" element={<KsefDlaJdg />} />
      <Route path="/przewodniki/ksiegowosc-online" element={<KsiegowoscOnline />} />
      <Route path="/przewodniki/e-doreczenia-jdg-2026" element={<EDoreczeniaJdg2026 />} />
      <Route
        path="/blog/ewidencja-przychodow-ryczalt-jak-prowadzic-2026"
        element={<EwidencjaPrzychodowRyczalt2026 />}
      />
      {/* Public MCP server install page (BOO-5261 U5). Polish on every locale
          route, canonical to /mcp, like the przewodniki. */}
      <Route path="/mcp" element={<Mcp />} />

      {/* Locale-prefixed routes */}
      <Route path="/en" element={<Index />} />
      <Route path="/en/privacy" element={<Privacy />} />
      <Route path="/en/terms" element={<Terms />} />
      <Route path="/en/cookies" element={<Cookies />} />
      <Route path="/en/biura" element={<Biura />} />
      <Route path="/en/jdg" element={<Jdg />} />
      <Route path="/en/wdrozenia" element={<Wdrozenia />} />
      <Route path="/en/ksef-automatycznie" element={<KsefAutomatycznie />} />
      <Route path="/en/ksiegowosc-ai" element={<KsiegowoscAi />} />
      <Route path="/en/przewodniki/kpir-elektroniczna-2026" element={<KpirElektroniczna2026 />} />
      <Route path="/en/przewodniki/kod-pkd-jak-wybrac" element={<KodPkdJakWybrac />} />
      <Route path="/en/przewodniki/ksef-dla-jdg" element={<KsefDlaJdg />} />
      <Route path="/en/przewodniki/ksiegowosc-online" element={<KsiegowoscOnline />} />
      <Route path="/en/przewodniki/e-doreczenia-jdg-2026" element={<EDoreczeniaJdg2026 />} />
      <Route path="/en/mcp" element={<Mcp />} />

      <Route path="/uk" element={<Index />} />
      <Route path="/uk/privacy" element={<Privacy />} />
      <Route path="/uk/terms" element={<Terms />} />
      <Route path="/uk/cookies" element={<Cookies />} />
      <Route path="/uk/biura" element={<Biura />} />
      <Route path="/uk/jdg" element={<Jdg />} />
      <Route path="/uk/wdrozenia" element={<Wdrozenia />} />
      <Route path="/uk/ksef-automatycznie" element={<KsefAutomatycznie />} />
      <Route path="/uk/ksiegowosc-ai" element={<KsiegowoscAi />} />
      <Route path="/uk/przewodniki/kpir-elektroniczna-2026" element={<KpirElektroniczna2026 />} />
      <Route path="/uk/przewodniki/kod-pkd-jak-wybrac" element={<KodPkdJakWybrac />} />
      <Route path="/uk/przewodniki/ksef-dla-jdg" element={<KsefDlaJdg />} />
      <Route path="/uk/przewodniki/ksiegowosc-online" element={<KsiegowoscOnline />} />
      <Route path="/uk/przewodniki/e-doreczenia-jdg-2026" element={<EDoreczeniaJdg2026 />} />
      <Route path="/uk/mcp" element={<Mcp />} />

      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

interface AppProps {
  /**
   * Set only by the build-time prerender (src/entry-server.tsx): renders the
   * route through a StaticRouter. The browser passes nothing and gets the
   * BrowserRouter, so the tree it hydrates is the same one the build rendered.
   */
  location?: string;
}

const App = ({ location }: AppProps) => {
  const routes = (
    <>
      <PostHogPageview />
      <ScrollToTop />
      <I18nProvider>
        <AppRoutes />
      </I18nProvider>
    </>
  );
  return (
    <Sentry.ErrorBoundary fallback={sentryFallback}>
      <HelmetProvider context={location === undefined ? undefined : {}}>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <Sonner />
            <LazyMotion features={domAnimation}>
              {/* WCAG 2.3.3: disable m.* animations globally for users with
                prefers-reduced-motion; complements per-component
                useReducedMotion() checks. */}
              <MotionConfig reducedMotion="user">
                {location === undefined ? (
                  <BrowserRouter>{routes}</BrowserRouter>
                ) : (
                  <StaticRouter location={location}>{routes}</StaticRouter>
                )}
              </MotionConfig>
            </LazyMotion>
            <Analytics />
            <SpeedInsights />
          </TooltipProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </Sentry.ErrorBoundary>
  );
};

export default App;
